import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Input, message, Form, Image, UploadFile, Space } from 'antd';
import { UploadOutlined, EditOutlined } from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import Upload, { UploadChangeParam } from 'antd/lib/upload';
import { useGlobalModel } from '@/model/globalModel';
import { getBaseImgLookUrl, getBaseImgUploadUrl, getBaseUploadData } from '@/utils/constants';
import { maintenanceService } from '../../maintenanceService';

interface FinalInspectionModalProps {
    visible: boolean;
    onCancel: () => void;
    billId: string;
    billCode: string;
}

export const FinalInspectionModal: React.FC<FinalInspectionModalProps> = ({
    visible,
    onCancel,
    billId,
    billCode
}) => {
    // 状态管理
    const [finalOpinion, setFinalOpinion] = useState<string>('');
    const [signature, setSignature] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [readSignature, setReadSignature] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    const sigCanvas = useRef<SignatureCanvas>(null);
    const { user: { token } } = useGlobalModel();
    const { TextArea } = Input;

    // 清空签名和文件列表当弹窗关闭时
    useEffect(() => {
        if (!visible) {
            clearSignature();
            setFileList([]);
            setFinalOpinion('');
        }
    }, [visible]);

    // 数据URL转Blob
    const dataURLToBlob = (dataURL: string) => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime || 'image/png' });
    };

    // 上传签名图片
    const uploadSignature = async (dataURL: string) => {
        if (!dataURL) {
            throw new Error('未签名！');
        }
        try {
            const blob = dataURLToBlob(dataURL);
            const formData = new FormData();
            formData.append('appCode', 'hd-vrm');
            formData.append('fileMode', '0');
            formData.append('file', blob, 'inspection_signature.png');
            const response = await fetch(`${getBaseImgUploadUrl()}`, {
                method: 'POST',
                body: formData,
                headers: { 'Authorization': token }
            });

            if (!response.ok) {
                throw new Error(`上传失败: ${response.status}`);
            }

            const result = await response.json();

            if (result && result.retData) {
                setSignature(result.retData);
                return result.retData;
            } else {
                throw new Error('服务器返回数据格式不正确');
            }
        } catch (error: any) {
            console.error('上传签名失败', error);
            throw new Error(error.message || '签名上传失败，请重试');
        }
    };

    // 处理签名变化
    const handleSignatureUpload = async () => {
        if (!readSignature) {
            message.error('请先完成签名');
            return false;
        }

        try {
            const dataURL = sigCanvas.current?.toDataURL() || '';

            if (dataURL) {
                const finalSignature = await uploadSignature(dataURL);
                return finalSignature;
            }

            return false;
        } catch (error: any) {
            message.error(error.message);
            console.error('处理签名错误', error);
            return false;
        }
    };

    // 清除签名
    const clearSignature = () => {
        setSignature('');
        sigCanvas.current?.clear();
        setReadSignature(false);
    };

    // 处理签名开始
    const onSignatureBegin = () => {
        setReadSignature(true);
    };

    // 文件上传处理
    const normFile = (e: UploadChangeParam) => {
        if (e?.file.status === 'done') {
            if (e?.file?.response?.retCode === 0) {
                const data: any = {
                    name: e.file.name,
                    size: e.file.size,
                    type: e.file.type,
                    uid: e?.file?.response?.retData,
                    status: 'uploaded',
                    url: getBaseImgLookUrl(e?.file?.response?.retData)
                };
                return [...e.fileList, data].filter(f => f.status !== 'error');
            } else {
                message.error('文件上传失败，请重新上传!');
                return e.fileList.filter(f => f.uid !== e.file.uid);
            }
        }
        if (e.file.status === 'error') {
            message.error('文件上传失败，请重新上传!');
            return e.fileList.filter(f => f.uid !== e.file.uid);
        }
        if (Array.isArray(e)) {
            return e;
        }
        return e.fileList;
    };

    // 图片预览
    const onPreview = (file: UploadFile<any>) => {
        const uid = file.uid || file.response?.retData;
        Modal.info({
            title: '图片查看',
            content: <Image style={{ width: '100%' }} src={getBaseImgLookUrl(uid)} alt="终检图片" />,
            okText: '确定',
            centered: true,
            width: 800,
        });
    };

    // 处理文件上传变化
    const handleFileChange = ({ fileList: newFileList }: UploadChangeParam) => {
        const processedList = newFileList.map(file => {
            if (file.status === 'done' && file.response?.retData) {
                return {
                    ...file,
                    uid: file.response.retData,
                    url: getBaseImgLookUrl(file.response.retData)
                };
            }
            return file;
        });
        setFileList(processedList);
    };

    // 验证表单
    const validateForm = () => {
        if (!readSignature) {
            message.warning('请完成终检签名');
            return false;
        }

        return true;
    };

    // 提交终检信息
    const handleSubmit = async () => {
        if (isSubmitting) return;

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);

            // 上传签名
            const finalSignature = await handleSignatureUpload();
            if (!finalSignature) {
                setIsSubmitting(false);
                return;
            }

            // 收集图片URL
            const finalMsg = fileList.map(file => file.uid);
            // 构建提交数据
            const submitData = {
                id: billId,
                finalOpinion,
                finalSignature,
                finalMsg: finalMsg.join(',')
            };

            // 提交终检信息
            const result = await maintenanceService.submitFinalInspection(submitData);

            if (result.success) {
                message.success('终检提交成功');
                onCancel();
            } else {
                message.error(`提交失败: ${result || '未知错误'}`);
            }
        } catch (error: any) {
            message.error(error.message || '提交失败，请重试');
            console.error('终检提交错误:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            title="终检确认"
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={600}
        >
            <div style={{ marginBottom: 20 }}>
                {/* 工单编号 */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 15 }}>
                    <span style={{ fontWeight: "bold", width: 100, display: "inline-block" }}>
                        工单编号:
                    </span>
                    <span>{billCode}</span>
                </div>

                {/* 终检意见 */}
                <div style={{ marginBottom: 20 }}>
                    <span style={{ fontWeight: "bold", width: 100, display: "inline-block", verticalAlign: "top" }}>
                        终检意见:
                    </span>
                    <TextArea
                        rows={4}
                        value={finalOpinion}
                        onChange={(e) => setFinalOpinion(e.target.value)}
                        placeholder="请输入终检意见..."
                        style={{ width: 'calc(100% - 100px)', display: 'inline-block' }}
                    />
                </div>

                {/* 终检图片 */}
                <div style={{ marginBottom: 10 }}>
                    <span style={{ fontWeight: "bold", width: 100, display: "inline-block", verticalAlign: "top" }}>
                        终检图片:
                    </span>
                    <Form.Item
                        name="inspectionFiles"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        style={{ marginLeft: 100, marginBottom: 0 }}
                    >
                        <Upload
                            onPreview={onPreview}
                            name="file"
                            action={getBaseImgUploadUrl()}
                            data={getBaseUploadData()}
                            listType="picture-card"
                            multiple
                            maxCount={4}
                            accept="image/*"
                            onChange={handleFileChange}
                        >
                            {fileList.length < 4 && (
                                <div>
                                    <UploadOutlined />
                                    <div style={{ marginTop: 8 }}>上传图片</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>
                    <div style={{ fontSize: 12, color: '#666', marginLeft: 100 }}>
                        最多上传4张图片，支持jpg、png格式
                    </div>
                </div>
                {/* 终检签名 */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontWeight: "bold", width: 100, display: "inline-block" }}>
                            终检签名: <span style={{ color: 'red' }}>*</span>
                        </span>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={clearSignature}
                            size="small"
                        >
                            清除签名
                        </Button>
                    </div>

                    <div style={{ border: '1px dashed #ddd', padding: 8, marginLeft: 100 }}>
                        <SignatureCanvas
                            ref={sigCanvas}
                            penColor="#000000"
                            backgroundColor="#ffffff"
                            canvasProps={{ width: 450, height: 200, className: 'signature-canvas' }}
                            onBegin={onSignatureBegin}
                        />
                    </div>
                    {!readSignature && (
                        <div style={{ color: 'red', fontSize: 12, marginTop: 5, marginLeft: 100 }}>
                            请完成签名确认
                        </div>
                    )}
                </div>

            </div>

            {/* 底部按钮 */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                {!isSubmitting ? (
                    <Space size="large">
                        <Button onClick={onCancel}>取消</Button>
                        <Button type="primary" onClick={handleSubmit}>
                            提交终检
                        </Button>
                    </Space>
                ) : (
                    <Button loading>提交中...</Button>
                )}
            </div>
        </Modal>
    );
};
